export const PRIVACY_POLICY_CONTENT = {
    version: '1.3.0',
    lastUpdated: 'July 2026',
    sections: [
        {
            title: '1. Introduction',
            content: `CourtSync ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our Service. By using the Service, you agree to the collection and use of information in accordance with this Privacy Policy.`,
        },
        {
            title: '2. Information We Collect',
            content: `2.1 Information You Provide: We collect information you provide directly to us, including:
- Account information (name, email, phone number)
- Payment information (processed through Stripe)
- Academy and student information
- Booking and lesson data
- Communication preferences
- Mobile phone numbers for transactional SMS when you provide one at academy registration or opt in later in your profile
- Information about minors when supplied by a parent, guardian, authorized academy user, or a public Starter request-booking form (for example when creating a student profile or submitting a lesson request)

2.2 Automatically Collected Information: We automatically collect certain information when you use the Service:
- Device information (IP address, browser type, device identifiers)
- Usage data (pages visited, features used, time spent)
- Log data (access times, error logs)
- Cookies and similar tracking technologies

2.3 Information from Third Parties: We may receive information from third-party services, including:
- Stripe (payment processing data)
- Firebase (authentication and analytics)
- Google Tag Manager and Google Analytics (website analytics and user behavior tracking)
- Social media platforms (if you use social login)
- Accounting integration providers (for example Xero and QuickBooks Online) when an academy enables those integrations
- Sentry and similar observability providers (diagnostic and error reporting data to keep the Service reliable)
- Twilio (transactional SMS delivery to guardians and students who have opted in)

Academy data is logically scoped to the academy that owns or configures the integration.`,
        },
        {
            title: '3. How We Use Your Information',
            content: `We use the information we collect to:
- Provide, maintain, and improve the Service
- Process payments and manage subscriptions
- Send you service-related communications, including transactional email and SMS about lessons, bookings, waitlists, and account activity when you have opted in
- Respond to your inquiries and provide customer support
- Detect, prevent, and address technical issues and fraud
- Comply with legal obligations
- Analyze usage patterns to improve user experience`,
        },
        {
            title: '4. Information Sharing and Disclosure',
            content: `4.1 Service Providers: We share information with third-party service providers who perform services on our behalf, including:
- Stripe (payment processing)
- Firebase (authentication, hosting, analytics)
- Google Tag Manager and Google Analytics (website analytics, user behavior tracking, and conversion tracking)
- Email service providers
- SMS service providers (transactional messages only, when you have opted in)
- Cloud hosting providers
- Accounting integration providers when an academy connects them (for example Xero or QuickBooks Online)
- Observability providers such as Sentry (incident diagnostics)

4.2 Legal Requirements: We may disclose your information if required by law or in response to valid requests by public authorities.

4.3 Business Transfers: In the event of a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.

4.4 With Your Consent: We may share your information with your explicit consent or at your direction.`,
        },
        {
            title: '5. Data Security',
            content: `We implement appropriate technical and organizational measures to protect your information against unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over the Internet or electronic storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.`,
        },
        {
            title: '6. Your Rights (GDPR and CCPA Compliance)',
            content: `6.1 Access: You have the right to access the personal information we hold about you.

6.2 Rectification: You have the right to correct inaccurate or incomplete information.

6.3 Erasure: You have the right to request deletion of your personal information, subject to legal and contractual obligations.

6.4 Data Portability: You have the right to receive your personal information in a structured, commonly used format.

6.5 Objection: You have the right to object to processing of your personal information for certain purposes.

6.6 Restriction: You have the right to request restriction of processing of your personal information.

6.7 Withdraw Consent: Where processing is based on consent, you have the right to withdraw consent at any time. For transactional SMS, you can reply STOP to a message, turn off SMS in your academy profile settings, or contact us at privacy@courtsync.io.

To exercise these rights, please contact us at privacy@courtsync.io.`,
        },
        {
            title: '7. California Privacy Rights (CCPA)',
            content: `If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):
- Right to know what personal information is collected, used, shared, or sold
- Right to delete personal information
- Right to opt-out of the sale of personal information (we do not sell personal information)
- Right to non-discrimination for exercising your privacy rights

To exercise these rights, please contact us at privacy@courtsync.io.`,
        },
        {
            title: '8. Cookies and Tracking Technologies',
            content: `8.1 Cookie Consent: When you first visit our website, you will be presented with a cookie consent banner that allows you to choose which categories of cookies you wish to accept. You can manage your cookie preferences at any time by clicking the "Cookie Settings" link in the footer of our website.

8.2 Cookie Categories: We use the following categories of cookies:

Necessary Cookies (Always Active):
These cookies are essential for the website to function properly and cannot be disabled. They enable basic features like page navigation, access to secure areas, and remembering your cookie preferences. These cookies do not store any personally identifiable information.

Analytics Cookies (Requires Consent):
These cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. We use:
- Google Tag Manager (GTM): A tag management system that allows us to manage and deploy marketing tags and analytics code
- Google Analytics: Collects data about page views, navigation patterns, time spent on pages, user interactions, device and browser information, and general geographic location

The data collected through analytics cookies is used to improve our website, understand user behavior, and measure the effectiveness of our marketing efforts. This information is transmitted to and stored by Google in accordance with Google's Privacy Policy.

Marketing Cookies (Requires Consent):
These cookies are used to track visitors across websites to display relevant advertisements. We currently do not use marketing cookies, but this option is available for future use. If we implement marketing cookies, we will update this policy and request your consent.

8.3 Managing Cookies: You have several options to manage cookies:
- Use our Cookie Consent Banner: Click "Cookie Settings" in the footer to customize your preferences
- Browser Settings: You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent
- Google Analytics Opt-out: Install the Google Analytics Opt-out Browser Add-on at https://tools.google.com/dlpage/gaoptout

Please note that if you disable cookies, you may not be able to use some portions of our Service.

8.4 Cookie Duration: 
- Necessary cookies: Session cookies (deleted when you close your browser) or up to 1 year
- Analytics cookies: Up to 2 years
- Your cookie consent preferences: Stored for 1 year, after which you will be asked to confirm your preferences again

8.5 Third-Party Cookies: In addition to our own cookies, we may use third-party cookies from service providers like Google Analytics and Google Tag Manager. These cookies are subject to the respective privacy policies of these third parties.`,
        },
        {
            title: '9. Children\'s Privacy',
            content: `The Service is intended for academy operators, guardians, and adult students booking lessons. We do not knowingly collect personal information directly from children under 13. Student profiles for minors are typically created when a parent, guardian, or authorized academy user registers the child or completes a booking on their behalf. If you believe a child has provided us personal information without appropriate authorization, please contact us immediately.`,
        },
        {
            title: '10. International Data Transfers',
            content: `Your information may be transferred to and maintained on computers located outside of your state, province, country, or other governmental jurisdiction where data protection laws may differ. By using the Service, you consent to the transfer of your information to our facilities and those third parties with whom we share it as described in this Privacy Policy.`,
        },
        {
            title: '11. Data Retention',
            content: `We retain your personal information for as long as necessary to provide the Service, comply with legal obligations, resolve disputes, and enforce our agreements. When we no longer need your information, we will securely delete or anonymize it.`,
        },
        {
            title: '12. Changes to This Privacy Policy',
            content: `We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes.`,
        },
        {
            title: '13. Contact Us',
            content: `If you have any questions about this Privacy Policy, please contact us at:
- Email: privacy@courtsync.io
- Address: Perth, Western Australia`,
        },
    ],
};
